import { yupResolver } from '@hookform/resolvers/yup'
import InfoBlock from 'components/UI/Blocks/InfoBlock'
import { Button } from 'components/UI/Buttons'
import Select from 'components/UI/Select'
import Typography from 'components/UI/Typography'
import Heading from 'components/UI/Typography/Heading'
import { useAppDispatch, useAppSelector } from 'hooks'
import { useCreditDecision } from 'hooks/useCreditDecision'
import { useThirdPartyPledgerStatus } from 'hooks/useThirdPartyPledgerStatus'
import { DecisionSubViews } from 'pages/Decision/constants'
import { Fragment, ReactNode, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useFetchClientPropertiesMutation } from 'redux/api/pledgeApi'
import { setProperty } from 'redux/store/slices/decisionSlice'
import { getActiveCreditApplication } from 'redux/store/slices/userSlice'
import { getThirdPartyNotificationsSelector } from 'redux/store/slices/wsSlice'
import { ApplicationEvents } from 'share/enums/events'
import type { PropertyUnit } from 'share/types/api/pledge'
import tw from 'twin.macro'
import { numberWithSpaces } from 'utils'
import Emitter from 'utils/emitter'
import * as yup from 'yup'

import AddThirdPartyPledger from './AddThirdPartyPledger'

type EstateSchemaValues = {
  estate: PropertyUnit | undefined
}

const estateSchema = yup.object({
  estate: yup.object<PropertyUnit>().required()
})

const EstateInfoBlock = ({ children, label }: { children: ReactNode; label: string }) => {
  return (
    <div>
      {label && (
        <Typography variant='subbody' twStyle={tw`text-grey-dark mx-3`}>
          {label}
        </Typography>
      )}
      <div tw='px-5 py-[15px] rounded-[15px] bg-stroke mt-1'>
        <Typography variant='subbody' twStyle={tw`text-grey-dark`}>
          {children}
        </Typography>
      </div>
    </div>
  )
}

const LoanPropertySelection = () => {
  // Tools
  const { t } = useTranslation()
  const dispatch = useAppDispatch()

  // Selectors
  const { id: creditID } = useAppSelector(getActiveCreditApplication)
  const { flow_status: pledgerFlowStatus } = useAppSelector(getThirdPartyNotificationsSelector)

  // Mutations
  const [fetchProperties, { data, isLoading: isPropertiesLoading }] = useFetchClientPropertiesMutation()

  // Custom hooks
  const { is3rdPartyPledgerApplied, onSubViewChange } = useCreditDecision()
  const { isPledgerConfirmationFlowDone } = useThirdPartyPledgerStatus()

  // Forms
  const {
    control,
    formState: { isValid },
    watch,
    handleSubmit
  } = useForm<EstateSchemaValues>({
    mode: 'onChange',
    resolver: yupResolver(estateSchema)
  })

  // Computed values
  const selectedPledge = watch('estate')
  const isSelectedPledge3rdParty = !!(selectedPledge?.pledger && selectedPledge.pledger)

  // Handlers
  const handleSelectedPropertyIdChange = (data: EstateSchemaValues) => {
    if (!data.estate) return
    dispatch(setProperty(data.estate))
    onSubViewChange(DecisionSubViews['CHOOSE-LOAN-PARAMS'])
  }

  useEffect(() => {
    const check = async () => {
      if (!creditID) return
      if (is3rdPartyPledgerApplied && pledgerFlowStatus) {
        await fetchProperties(creditID).unwrap()
        Emitter.emit(ApplicationEvents.FETCH_FLOW_IDS_AND_NAVIGATE)
      }
    }

    check()
  }, [creditID, fetchProperties, pledgerFlowStatus, is3rdPartyPledgerApplied])

  useEffect(() => {
    if (creditID) {
      fetchProperties(creditID).unwrap()
    }
    return () => {
      if (!creditID) return
      fetchProperties(creditID).abort()
    }
  }, [creditID, fetchProperties])

  return (
    <Fragment>
      <InfoBlock twStyle={tw`flex flex-col gap-4`}>
        <Heading variant='subtitle' twStyle={tw`leading-5`}>
          {isSelectedPledge3rdParty ? t('3rd-party-pledger.pledge-info') : t('apartment-info')}
        </Heading>
        <Select
          control={control}
          name='estate'
          placeholder={t('choose-apartment2')}
          label={t('choose-apartment')}
          isSearchable
          isDisabled={isPropertiesLoading}
          options={data}
          noOptionsMessage={() => t('flow-not-founded')}
          getOptionLabel={option => option.address}
          getOptionValue={option => option.id}
        />
        {selectedPledge ? (
          <Fragment>
            <EstateInfoBlock label={t('full-adress')}>{selectedPledge?.address}</EstateInfoBlock>
            <EstateInfoBlock label={t('estimating-cost')}>{`${numberWithSpaces(
              Number(selectedPledge?.bank_price).toFixed(0)
            )} ₸`}</EstateInfoBlock>
            <EstateInfoBlock label={t('cadastrial-number')}>{selectedPledge?.cadastral_number}</EstateInfoBlock>
          </Fragment>
        ) : null}
        <Button
          variant='secondary'
          disabled={!isValid}
          twStyle={tw`self-end py-3 w-[unset]`}
          onClick={handleSubmit(handleSelectedPropertyIdChange)}
        >
          <Typography variant='caption' weight='bold'>
            {t('flow-choose')}
          </Typography>
        </Button>
      </InfoBlock>
      <AddThirdPartyPledger key={'' + is3rdPartyPledgerApplied} isThirdPartyFlowDone={isPledgerConfirmationFlowDone} />
    </Fragment>
  )
}

export default LoanPropertySelection
